import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Loader2, ArrowLeft, UserPlus, Users, ExternalLink, Share2, Upload, FileText, Edit, ChevronDown, MapPin, Briefcase, DollarSign, Calendar, Building2, Clock, Globe, CheckSquare, X, Send, Sparkles } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import RecruiterLayout from '@/components/RecruiterLayout';
import { KanbanBoard, useKanban, AddCandidateDialog, KanbanCandidate, SendToClientDialog } from '@/components/kanban';
import CandidateDetailDialog from '@/components/CandidateDetailDialog';
import BulkResumeUpload from '@/components/BulkResumeUpload';
import ParsedCandidatesView from '@/components/ParsedCandidatesView';
import JobForm from '@/components/JobForm';
import RichTextDisplay from '@/components/ui/rich-text-display';
import { CandidateSearchDialog } from '@/components/job';

const JobKanban = () => {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [job, setJob] = useState<any>(null);
  const [jobLoading, setJobLoading] = useState(true);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [selectedCandidateId, setSelectedCandidateId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('kanban');
  const [detailsOpen, setDetailsOpen] = useState(true);
  
  // Selection mode state
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedCandidates, setSelectedCandidates] = useState<KanbanCandidate[]>([]);
  const [sendToClientOpen, setSendToClientOpen] = useState(false);
  const [searchDialogOpen, setSearchDialogOpen] = useState(false);

  const {
    columns,
    candidates,
    unstagedCandidates,
    loading: kanbanLoading,
    moveCandidate,
    addCandidateToJob,
    updateStage,
    addStage,
    deleteStage,
    refresh,
  } = useKanban(jobId || '');

  useEffect(() => {
    if (jobId) {
      fetchJob();
    }
  }, [jobId]);

  const fetchJob = async () => {
    const { data, error } = await supabase
      .from('jobs')
      .select(`
        *,
        clients (id, name),
        assigned_user:app_users!jobs_assigned_to_fkey (id, full_name)
      `)
      .eq('id', jobId)
      .single();

    if (error) {
      toast({
        title: 'Error loading job',
        description: error.message,
        variant: 'destructive',
      });
      navigate('/recruiter/jobs');
      return;
    }

    setJob(data);
    setJobLoading(false);
  };

  const copyPublicLink = () => {
    const link = `${window.location.origin}/jobs/${jobId}`;
    navigator.clipboard.writeText(link);
    toast({ title: 'Link copied!' });
  };

  const formatSalary = () => {
    if (!job.salary_min && !job.salary_max) return null;
    const currency = job.salary_currency || 'USD';
    if (job.salary_min && job.salary_max) {
      return `${currency} ${job.salary_min.toLocaleString()} - ${job.salary_max.toLocaleString()}`;
    }
    if (job.salary_min) return `${currency} ${job.salary_min.toLocaleString()}+`;
    return `Up to ${currency} ${job.salary_max.toLocaleString()}`;
  };

  if (jobLoading) {
    return (
      <RecruiterLayout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </RecruiterLayout>
    );
  }

  if (!job) {
    return (
      <RecruiterLayout>
        <div className="text-center py-12">
          <h2 className="text-xl font-semibold mb-2">Job not found</h2>
          <Button variant="outline" onClick={() => navigate('/recruiter/jobs')}>
            Back to Jobs
          </Button>
        </div>
      </RecruiterLayout>
    );
  }

  const existingCandidateIds = candidates.map(c => c.id);

  const toggleCandidateSelection = (candidate: KanbanCandidate) => {
    setSelectedCandidates(prev => {
      const isSelected = prev.some(c => c.id === candidate.id);
      if (isSelected) {
        return prev.filter(c => c.id !== candidate.id);
      }
      return [...prev, candidate];
    });
  };

  const selectAllCandidates = () => {
    setSelectedCandidates(candidates);
  };

  const clearSelection = () => {
    setSelectedCandidates([]);
  };

  const toggleSelectionMode = () => {
    if (selectionMode) {
      // Exit selection mode
      setSelectionMode(false);
      setSelectedCandidates([]);
    } else {
      // Enter selection mode
      setSelectionMode(true);
    }
  };
  return (
    <RecruiterLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <Button variant="ghost" size="sm" onClick={() => navigate('/recruiter/jobs')}>
                <ArrowLeft className="h-4 w-4 mr-1" />
                Jobs
              </Button>
            </div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold">{job.title}</h1>
              <Badge variant={job.status === 'published' ? 'default' : 'secondary'}>
                {job.status}
              </Badge>
            </div>
            <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground flex-wrap">
              {job.clients && (
                <span className="flex items-center gap-1">
                  <Building2 className="h-3 w-3" />
                  {job.clients.name}
                </span>
              )}
              {job.assigned_user && (
                <span className="flex items-center gap-1">
                  <Users className="h-3 w-3" />
                  {job.assigned_user.full_name}
                </span>
              )}
            </div>
          </div>

          <div className="flex gap-2">
            <Button 
              variant={selectionMode ? "default" : "outline"} 
              size="sm" 
              onClick={toggleSelectionMode}
            >
              <CheckSquare className="h-4 w-4 mr-1" />
              {selectionMode ? 'Exit Selection' : 'Select'}
            </Button>
            <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
              <Edit className="h-4 w-4 mr-1" />
              Edit
            </Button>
            <Button variant="outline" size="sm" onClick={copyPublicLink}>
              <Share2 className="h-4 w-4 mr-1" />
              Share
            </Button>
            <Link to={`/jobs/${jobId}`} target="_blank">
              <Button variant="outline" size="sm">
                <ExternalLink className="h-4 w-4 mr-1" />
                Preview
              </Button>
            </Link>
            <Button variant="outline" onClick={() => setBulkUploadOpen(true)}>
              <Upload className="h-4 w-4 mr-1" />
              Bulk Upload
            </Button>
            <Button variant="secondary" onClick={() => setSearchDialogOpen(true)}>
              <Sparkles className="h-4 w-4 mr-1" />
              Find Candidates
            </Button>
            <Button onClick={() => setAddDialogOpen(true)}>
              <UserPlus className="h-4 w-4 mr-1" />
              Add Candidate
            </Button>
          </div>
        </div>

        {/* Job Details Collapsible */}
        <Collapsible open={detailsOpen} onOpenChange={setDetailsOpen}>
          <Card className="p-4">
            <CollapsibleTrigger asChild>
              <Button variant="ghost" className="w-full flex items-center justify-between p-0 h-auto hover:bg-transparent">
                <span className="font-semibold text-sm">Job Details</span>
                <ChevronDown className={`h-4 w-4 transition-transform ${detailsOpen ? 'rotate-180' : ''}`} />
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="pt-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                {job.location && (
                  <div className="flex items-start gap-2">
                    <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
                    <div>
                      <div className="text-xs text-muted-foreground">Location</div>
                      <div className="text-sm font-medium">{job.location}</div>
                    </div>
                  </div>
                )}
                {job.job_type && (
                  <div className="flex items-start gap-2">
                    <Briefcase className="h-4 w-4 text-muted-foreground mt-0.5" />
                    <div>
                      <div className="text-xs text-muted-foreground">Job Type</div>
                      <div className="text-sm font-medium capitalize">{job.job_type.replace('_', ' ')}</div>
                    </div>
                  </div>
                )}
                {job.work_mode && (
                  <div className="flex items-start gap-2">
                    <Globe className="h-4 w-4 text-muted-foreground mt-0.5" />
                    <div>
                      <div className="text-xs text-muted-foreground">Work Mode</div>
                      <div className="text-sm font-medium capitalize">{job.work_mode.replace('_', ' ')}</div>
                    </div>
                  </div>
                )}
                {formatSalary() && (
                  <div className="flex items-start gap-2">
                    <DollarSign className="h-4 w-4 text-muted-foreground mt-0.5" />
                    <div>
                      <div className="text-xs text-muted-foreground">Salary Range</div>
                      <div className="text-sm font-medium">{formatSalary()}</div>
                    </div>
                  </div>
                )}
                {job.created_at && (
                  <div className="flex items-start gap-2">
                    <Calendar className="h-4 w-4 text-muted-foreground mt-0.5" />
                    <div>
                      <div className="text-xs text-muted-foreground">Created</div>
                      <div className="text-sm font-medium">{new Date(job.created_at).toLocaleDateString()}</div>
                    </div>
                  </div>
                )}
                {job.published_at && (
                  <div className="flex items-start gap-2">
                    <Clock className="h-4 w-4 text-muted-foreground mt-0.5" />
                    <div>
                      <div className="text-xs text-muted-foreground">Published</div>
                      <div className="text-sm font-medium">{new Date(job.published_at).toLocaleDateString()}</div>
                    </div>
                  </div>
                )}
              </div>
              
              {job.description && (
                <div className="border-t pt-4 mt-4">
                  <h4 className="text-sm font-semibold mb-2">Description</h4>
                  <RichTextDisplay content={job.description} className="text-sm text-muted-foreground" />
                </div>
              )}
              
              {job.requirements && (
                <div className="border-t pt-4 mt-4">
                  <h4 className="text-sm font-semibold mb-2">Requirements</h4>
                  <RichTextDisplay content={job.requirements} className="text-sm text-muted-foreground" />
                </div>
              )}
            </CollapsibleContent>
          </Card>
        </Collapsible>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="p-4">
            <div className="text-2xl font-bold">{candidates.length}</div>
            <div className="text-sm text-muted-foreground">Total Candidates</div>
          </Card>
          {columns.slice(0, 3).map(col => (
            <Card key={col.stage.id} className="p-4">
              <div className="text-2xl font-bold">{col.candidates.length}</div>
              <div className="text-sm text-muted-foreground">{col.stage.name}</div>
            </Card>
          ))}
        </div>

        {/* Selection Action Bar */}
        {selectionMode && (
          <Card className="p-3 flex items-center justify-between bg-primary/5 border-primary/20">
            <div className="flex items-center gap-4">
              <span className="text-sm font-medium">
                {selectedCandidates.length} of {candidates.length} selected
              </span>
              <Button variant="ghost" size="sm" onClick={selectAllCandidates}>
                Select All
              </Button>
              <Button variant="ghost" size="sm" onClick={clearSelection}>
                Clear
              </Button>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => setSendToClientOpen(true)}
                disabled={selectedCandidates.length === 0}
              >
                <Send className="h-4 w-4 mr-1" />
                Send to Client ({selectedCandidates.length})
              </Button>
              <Button variant="outline" size="sm" onClick={toggleSelectionMode}>
                <X className="h-4 w-4 mr-1" />
                Cancel
              </Button>
            </div>
          </Card>
        )}

        {/* Tabs for Kanban and Parsed Candidates */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="kanban">Kanban Board</TabsTrigger>
            <TabsTrigger value="parsed">
              <FileText className="h-4 w-4 mr-1" />
              Parsed Candidates
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="kanban" className="mt-4">
            <KanbanBoard
              columns={columns}
              unstagedCandidates={unstagedCandidates}
              loading={kanbanLoading}
              onMoveCandidate={moveCandidate}
              onAddCandidate={() => setAddDialogOpen(true)}
              onUpdateStage={updateStage}
              onAddStage={addStage}
              onDeleteStage={deleteStage}
              onCandidateClick={(candidate) => setSelectedCandidateId(candidate.id)}
              selectionMode={selectionMode}
              selectedCandidates={selectedCandidates}
              onToggleSelection={toggleCandidateSelection}
              onSelectAll={selectAllCandidates}
              onClearSelection={clearSelection}
            />
          </TabsContent>
          
          <TabsContent value="parsed" className="mt-4">
            <ParsedCandidatesView jobId={jobId} showAsTab />
          </TabsContent>
        </Tabs>

        {/* Edit Job Form */}
        <JobForm
          job={job}
          open={editOpen}
          onOpenChange={setEditOpen}
          onSuccess={() => {
            fetchJob();
            setEditOpen(false);
          }}
        />

        {/* Add Candidate Dialog */}
        <AddCandidateDialog
          open={addDialogOpen}
          onOpenChange={setAddDialogOpen}
          jobId={jobId || ''}
          existingCandidateIds={existingCandidateIds}
          onAddCandidate={addCandidateToJob}
        />

        {/* Bulk Resume Upload Dialog */}
        <BulkResumeUpload
          open={bulkUploadOpen}
          onOpenChange={setBulkUploadOpen}
          jobId={jobId}
          onSuccess={() => refresh()}
        />

        {/* Candidate Detail Dialog */}
        <CandidateDetailDialog
          candidateId={selectedCandidateId || ''}
          open={!!selectedCandidateId}
          onOpenChange={(open) => !open && setSelectedCandidateId(null)}
        />

        {/* Send to Client Dialog */}
        <SendToClientDialog
          open={sendToClientOpen}
          onOpenChange={setSendToClientOpen}
          selectedCandidates={selectedCandidates}
          jobId={jobId || ''}
          jobTitle={job.title}
          clientId={job.client_id}
          onSuccess={() => {
            setSelectionMode(false);
            setSelectedCandidates([]);
          }}
        />

        {/* Candidate Search Dialog */}
        <CandidateSearchDialog
          open={searchDialogOpen}
          onOpenChange={setSearchDialogOpen}
          jobId={jobId || ''}
          jobTitle={job.title}
          onAddCandidates={async (candidateIds) => {
            for (const id of candidateIds) {
              await addCandidateToJob(id);
            }
            refresh();
          }}
        />
      </div>
    </RecruiterLayout>
  );
};

export default JobKanban;
